import { useState, useEffect, useRef } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { ShoppingCart, User, Menu, X, ChevronDown, Battery, Wrench, Package, LogOut, Settings, Globe, Search } from 'lucide-react';
import { useAuth } from '../context/AuthContext';
import { useCart } from '../context/CartContext';
import { useLanguage } from '../context/LanguageContext';
import axios from 'axios';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
  DropdownMenuSeparator,
} from '../components/ui/dropdown-menu';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const Header = () => {
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [searchOpen, setSearchOpen] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  const [suggestions, setSuggestions] = useState([]);
  const [showSuggestions, setShowSuggestions] = useState(false);
  const searchRef = useRef(null);
  const { user, logout, isAdmin } = useAuth();
  const { cartCount } = useCart();
  const { language, switchLanguage, t } = useLanguage();
  const navigate = useNavigate();

  // Fetch suggestions when search query changes
  useEffect(() => {
    const fetchSuggestions = async () => {
      if (searchQuery.length < 2) {
        setSuggestions([]);
        return;
      }
      
      try {
        const response = await axios.get(`${API}/products/suggestions?q=${encodeURIComponent(searchQuery)}`);
        setSuggestions(response.data.suggestions || []);
        setShowSuggestions(true);
      } catch (error) {
        console.error('Failed to fetch suggestions:', error);
        setSuggestions([]);
      }
    };

    const debounceTimer = setTimeout(fetchSuggestions, 200);
    return () => clearTimeout(debounceTimer);
  }, [searchQuery]);

  // Close suggestions when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (searchRef.current && !searchRef.current.contains(event.target)) {
        setShowSuggestions(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const handleLogout = () => {
    logout();
    navigate('/');
  };

  const handleSearch = (e) => {
    e.preventDefault();
    if (searchQuery.trim()) {
      navigate(`/shop?search=${encodeURIComponent(searchQuery.trim())}`);
      setSearchQuery('');
      setSearchOpen(false);
      setShowSuggestions(false);
    }
  };

  const handleSuggestionClick = (productId) => {
    navigate(`/product/${productId}`);
    setSearchQuery('');
    setSearchOpen(false);
    setShowSuggestions(false);
  };

  const formatPrice = (price) => {
    return new Intl.NumberFormat('pl-PL', { style: 'currency', currency: 'PLN' }).format(price);
  };

  const categories = [
    { id: 'dewalt-batteries', name: t.nav.powerTools, icon: Battery },
    { id: 'milwaukee-batteries', name: t.nav.handTools, icon: Battery },
    { id: 'makita-batteries', name: t.nav.accessories, icon: Battery },
    { id: 'tools-accessories', name: t.nav.toolsAccessories, icon: Wrench },
  ];

  return (
    <header className="sticky top-0 z-50 bg-[#050505]/90 backdrop-blur-xl border-b border-zinc-800" data-testid="header">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="flex items-center justify-between h-16 md:h-20">
          {/* Logo */}
          <Link to="/" className="flex items-center gap-2" data-testid="logo-link">
            <span className="font-heading text-2xl md:text-3xl text-yellow-400 uppercase tracking-tight">
              Narzędzia Pro
            </span>
          </Link>

          {/* Desktop Navigation */}
          <nav className="hidden md:flex items-center gap-8">
            <Link 
              to="/" 
              className="font-mono text-sm tracking-widest uppercase text-zinc-400 hover:text-white transition-colors"
              data-testid="nav-home"
            >
              {t.nav.home}
            </Link>

            {/* Shop Dropdown */}
            <DropdownMenu>
              <DropdownMenuTrigger className="flex items-center gap-1 font-mono text-sm tracking-widest uppercase text-zinc-400 hover:text-white transition-colors" data-testid="nav-shop-dropdown">
                {t.nav.shop} <ChevronDown size={14} />
              </DropdownMenuTrigger>
              <DropdownMenuContent className="bg-[#111111] border-zinc-800 rounded-none min-w-[200px]">
                <DropdownMenuItem asChild>
                  <Link to="/shop" className="flex items-center gap-2 cursor-pointer text-zinc-300 hover:text-white hover:bg-[#1A1A1A]" data-testid="nav-all-products">
                    {t.nav.allProducts}
                  </Link>
                </DropdownMenuItem>
                <DropdownMenuSeparator className="bg-zinc-800" />
                {categories.map((cat) => (
                  <DropdownMenuItem key={cat.id} asChild>
                    <Link 
                      to={`/shop/${cat.id}`} 
                      className="flex items-center gap-2 cursor-pointer text-zinc-300 hover:text-white hover:bg-[#1A1A1A]"
                      data-testid={`nav-category-${cat.id}`}
                    >
                      <cat.icon size={16} className="text-yellow-400" />
                      {cat.name}
                    </Link>
                  </DropdownMenuItem>
                ))}
              </DropdownMenuContent>
            </DropdownMenu>

            <Link 
              to="/about" 
              className="font-mono text-sm tracking-widest uppercase text-zinc-400 hover:text-white transition-colors"
              data-testid="nav-about"
            >
              {t.nav.about}
            </Link>
            <Link 
              to="/contact" 
              className="font-mono text-sm tracking-widest uppercase text-zinc-400 hover:text-white transition-colors"
              data-testid="nav-contact"
            >
              {t.nav.contact}
            </Link>
          </nav>

          {/* Desktop Actions */}
          <div className="hidden md:flex items-center gap-4">
            {/* Search */}
            <div className="relative" ref={searchRef}>
              {searchOpen ? (
                <div className="relative">
                  <form onSubmit={handleSearch} className="flex items-center">
                    <input
                      type="text"
                      value={searchQuery}
                      onChange={(e) => setSearchQuery(e.target.value)}
                      onFocus={() => suggestions.length > 0 && setShowSuggestions(true)}
                      placeholder="Szukaj..."
                      className="bg-[#111111] border border-zinc-700 text-white px-3 py-1.5 text-sm w-64 focus:border-yellow-400 focus:outline-none"
                      autoFocus
                      data-testid="search-input"
                    />
                    <button 
                      type="button" 
                      onClick={() => { setSearchOpen(false); setShowSuggestions(false); }}
                      className="p-2 text-zinc-400 hover:text-white"
                    >
                      <X size={18} />
                    </button>
                  </form>
                  
                  {/* Autocomplete Suggestions */}
                  {showSuggestions && suggestions.length > 0 && (
                    <div className="absolute top-full left-0 w-80 bg-[#111111] border border-zinc-700 shadow-2xl z-50 max-h-96 overflow-y-auto">
                      {suggestions.map((product) => (
                        <div
                          key={product.id}
                          onClick={() => handleSuggestionClick(product.id)}
                          className="flex items-center gap-3 p-3 hover:bg-[#1A1A1A] cursor-pointer border-b border-zinc-800 last:border-b-0 transition-colors"
                          data-testid={`suggestion-${product.id}`}
                        >
                          {product.image_url && (
                            <img 
                              src={product.image_url} 
                              alt={product.name}
                              className="w-12 h-12 object-cover bg-zinc-800"
                            />
                          )}
                          <div className="flex-1 min-w-0">
                            <p className="text-sm text-white truncate">{product.name}</p>
                            <p className="text-xs text-zinc-500">{product.category}</p>
                          </div>
                          <span className="text-sm text-yellow-400 font-bold whitespace-nowrap">
                            {formatPrice(product.price)}
                          </span>
                        </div>
                      ))}
                      <div 
                        onClick={handleSearch}
                        className="p-3 text-center text-sm text-zinc-400 hover:text-yellow-400 hover:bg-[#1A1A1A] cursor-pointer transition-colors"
                      >
                        {language === 'pl' ? 'Zobacz wszystkie wyniki' : 'Показать все результаты'} →
                      </div>
                    </div>
                  )}
                </div>
              ) : (
                <button 
                  onClick={() => setSearchOpen(true)}
                  className="p-2 text-zinc-400 hover:text-white transition-colors"
                  data-testid="search-btn"
                >
                  <Search size={20} />
                </button>
              )}
            </div>

            {/* Language Switcher */}
            <DropdownMenu>
              <DropdownMenuTrigger className="flex items-center gap-1 font-mono text-sm tracking-widest uppercase text-zinc-400 hover:text-white transition-colors p-2" data-testid="language-switcher">
                <Globe size={18} />
                <span className="text-xs">{language.toUpperCase()}</span>
              </DropdownMenuTrigger>
              <DropdownMenuContent className="bg-[#111111] border-zinc-800 rounded-none min-w-[120px]">
                <DropdownMenuItem 
                  onClick={() => switchLanguage('pl')}
                  className={`cursor-pointer hover:bg-[#1A1A1A] ${language === 'pl' ? 'text-yellow-400' : 'text-zinc-300 hover:text-white'}`}
                  data-testid="lang-pl"
                >
                  🇵🇱 Polski
                </DropdownMenuItem>
                <DropdownMenuItem 
                  onClick={() => switchLanguage('ru')}
                  className={`cursor-pointer hover:bg-[#1A1A1A] ${language === 'ru' ? 'text-yellow-400' : 'text-zinc-300 hover:text-white'}`}
                  data-testid="lang-ru"
                >
                  🇷🇺 Русский
                </DropdownMenuItem>
              </DropdownMenuContent>
            </DropdownMenu>

            <Link 
              to="/cart" 
              className="relative p-2 text-zinc-400 hover:text-white transition-colors"
              data-testid="cart-link"
            >
              <ShoppingCart size={24} strokeWidth={2} />
              {cartCount > 0 && (
                <span className="absolute -top-1 -right-1 bg-yellow-400 text-black text-xs font-bold w-5 h-5 flex items-center justify-center" data-testid="cart-count">
                  {cartCount}
                </span>
              )}
            </Link>

            {user ? (
              <DropdownMenu>
                <DropdownMenuTrigger className="flex items-center gap-2 font-mono text-sm tracking-widest uppercase text-zinc-400 hover:text-white transition-colors" data-testid="user-menu-trigger">
                  <User size={20} />
                  {user.name}
                </DropdownMenuTrigger>
                <DropdownMenuContent className="bg-[#111111] border-zinc-800 rounded-none min-w-[180px]">
                  <DropdownMenuItem asChild>
                    <Link to="/orders" className="flex items-center gap-2 cursor-pointer text-zinc-300 hover:text-white hover:bg-[#1A1A1A]" data-testid="nav-orders">
                      <Package size={16} />
                      {t.nav.myOrders}
                    </Link>
                  </DropdownMenuItem>
                  {isAdmin && (
                    <DropdownMenuItem asChild>
                      <Link to="/admin" className="flex items-center gap-2 cursor-pointer text-zinc-300 hover:text-white hover:bg-[#1A1A1A]" data-testid="nav-admin">
                        <Settings size={16} />
                        {t.nav.adminPanel}
                      </Link>
                    </DropdownMenuItem>
                  )}
                  <DropdownMenuSeparator className="bg-zinc-800" />
                  <DropdownMenuItem onClick={handleLogout} className="flex items-center gap-2 cursor-pointer text-zinc-300 hover:text-white hover:bg-[#1A1A1A]" data-testid="logout-btn">
                    <LogOut size={16} />
                    {t.nav.logout}
                  </DropdownMenuItem>
                </DropdownMenuContent>
              </DropdownMenu>
            ) : (
              <Link 
                to="/login" 
                className="font-mono text-sm tracking-widest uppercase text-zinc-400 hover:text-white transition-colors"
                data-testid="login-link"
              >
                {t.nav.login}
              </Link>
            )}
          </div>

          {/* Mobile Menu Button */}
          <div className="flex md:hidden items-center gap-4">
            {/* Mobile Language Switcher */}
            <button 
              onClick={() => switchLanguage(language === 'pl' ? 'ru' : 'pl')}
              className="p-2 text-zinc-400 font-mono text-xs"
              data-testid="mobile-lang-switch"
            >
              {language === 'pl' ? '🇷🇺 RU' : '🇵🇱 PL'}
            </button>

            <Link to="/cart" className="relative p-2 text-zinc-400" data-testid="mobile-cart-link">
              <ShoppingCart size={24} />
              {cartCount > 0 && (
                <span className="absolute -top-1 -right-1 bg-yellow-400 text-black text-xs font-bold w-5 h-5 flex items-center justify-center">
                  {cartCount}
                </span>
              )}
            </Link>
            <button 
              onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
              className="p-2 text-zinc-400"
              data-testid="mobile-menu-btn"
            >
              {mobileMenuOpen ? <X size={24} /> : <Menu size={24} />}
            </button>
          </div>
        </div>

        {/* Mobile Menu */}
        {mobileMenuOpen && (
          <div className="md:hidden border-t border-zinc-800 py-4" data-testid="mobile-menu">
            <nav className="flex flex-col gap-4">
              <Link to="/" onClick={() => setMobileMenuOpen(false)} className="font-mono text-sm tracking-widest uppercase text-zinc-400 py-2">
                {t.nav.home}
              </Link>
              <Link to="/shop" onClick={() => setMobileMenuOpen(false)} className="font-mono text-sm tracking-widest uppercase text-zinc-400 py-2">
                {t.nav.allProducts}
              </Link>
              {categories.map((cat) => (
                <Link 
                  key={cat.id}
                  to={`/shop/${cat.id}`} 
                  onClick={() => setMobileMenuOpen(false)}
                  className="font-mono text-sm tracking-widest uppercase text-zinc-400 py-2 pl-4 flex items-center gap-2"
                >
                  <cat.icon size={16} className="text-yellow-400" />
                  {cat.name}
                </Link>
              ))}
              <Link to="/about" onClick={() => setMobileMenuOpen(false)} className="font-mono text-sm tracking-widest uppercase text-zinc-400 py-2">
                {t.nav.about}
              </Link>
              <Link to="/contact" onClick={() => setMobileMenuOpen(false)} className="font-mono text-sm tracking-widest uppercase text-zinc-400 py-2">
                {t.nav.contact}
              </Link>
              <div className="border-t border-zinc-800 pt-4 mt-2">
                {user ? (
                  <>
                    <Link to="/orders" onClick={() => setMobileMenuOpen(false)} className="font-mono text-sm tracking-widest uppercase text-zinc-400 py-2 block">
                      {t.nav.myOrders}
                    </Link>
                    {isAdmin && (
                      <Link to="/admin" onClick={() => setMobileMenuOpen(false)} className="font-mono text-sm tracking-widest uppercase text-zinc-400 py-2 block">
                        {t.nav.adminPanel}
                      </Link>
                    )}
                    <button onClick={() => { handleLogout(); setMobileMenuOpen(false); }} className="font-mono text-sm tracking-widest uppercase text-zinc-400 py-2 block w-full text-left">
                      {t.nav.logout}
                    </button>
                  </>
                ) : (
                  <Link to="/login" onClick={() => setMobileMenuOpen(false)} className="font-mono text-sm tracking-widest uppercase text-zinc-400 py-2 block">
                    {t.nav.login} / {t.auth.register}
                  </Link>
                )}
              </div>
            </nav>
          </div>
        )}
      </div>
    </header>
  );
};

export default Header;
